<template>
  <base-modal-component>
    <template v-slot:header>
      <span class="md-subheading u-typography-color u-color-white">
        Crop image ({{ cropWidth }}x{{ cropHeight }})
      </span>
    </template>

    <template v-slot:body>
      <div class="crop-container">
        <img ref="image" :src="imageSrc" alt="Image to crop">
      </div>
    </template>

    <template v-slot:footer>
      <div class="u-mr-xs">
        <generic-button
          variation="grey"
          @click="close()"
        >
          Cancel
        </generic-button>
      </div>
      <generic-button
        variation="red"
        @click="crop()"
      >
        Crop &amp; Save
      </generic-button>
    </template>
  </base-modal-component>
</template>

<script>
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';
import BaseModalComponent from '../modal/BaseModalComponent';
import GenericButton from '../buttons/GenericButton';

export default {
  name: 'ImageCropModalComponent',

  components: {
    BaseModalComponent,
    GenericButton,
  },

  props: {
    imageSrc: {
      type: String,
      required: true,
    },
    fileName: {
      type: String,
      required: true,
    },
    fileType: {
      type: String,
      required: false,
      default: 'image/png',
    },
    cropWidth: {
      type: Number,
      required: true,
    },
    cropHeight: {
      type: Number,
      required: true,
    },
    onCrop: {
      required: true,
      type: Function,
    },
    onClose: {
      required: true,
      type: Function,
    },
  },

  data() {
    return {
      cropper: null,
    };
  },

  mounted() {
    this.cropper = new Cropper(this.$refs.image, {
      aspectRatio: this.cropWidth / this.cropHeight,
      viewMode: 1,
      dragMode: 'move',
      background: false,
      autoCropArea: 0.85,
      responsive: true,
      guides: true,
    });
  },

  beforeDestroy() {
    if (this.cropper) {
      this.cropper.destroy();
      this.cropper = null;
    }
  },

  methods: {
    close() {
      this.onClose();
      this.$emit('close');
    },

    crop() {
      if (!this.cropper) {
        return;
      }

      const canvas = this.cropper.getCroppedCanvas({
        width: this.cropWidth,
        height: this.cropHeight,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });

      canvas.toBlob((blob) => {
        const croppedFile = new File([blob], this.fileName, { type: this.fileType });
        this.onCrop(croppedFile);
        this.$emit('close');
      }, this.fileType);
    },
  },
};
</script>

<style lang="scss" scoped>
.crop-container {
  box-sizing: border-box;
  width: 100%;
  max-width: 680px;
  height: 50vh;
  max-height: 680px;
  padding: 1.5rem;

  img {
    display: block;
    max-width: 100%;
  }
}

::v-deep {
  .cropper-view-box,
  .cropper-face {
    outline-color: #fff;
  }

  .cropper-point {
    width: 12px;
    height: 12px;
    background-color: #39f;
    border-radius: 50%;
    opacity: 0.9;
  }

  .cropper-point.point-e {
    top: 50%;
    right: -6px;
    margin-top: -6px;
  }

  .cropper-point.point-n {
    top: -6px;
    left: 50%;
    margin-left: -6px;
  }

  .cropper-point.point-w {
    top: 50%;
    left: -6px;
    margin-top: -6px;
  }

  .cropper-point.point-s {
    bottom: -6px;
    left: 50%;
    margin-left: -6px;
  }

  .cropper-point.point-ne {
    top: -6px;
    right: -6px;
  }

  .cropper-point.point-nw {
    top: -6px;
    left: -6px;
  }

  .cropper-point.point-sw {
    bottom: -6px;
    left: -6px;
  }

  .cropper-point.point-se {
    right: -6px !important;
    bottom: -6px !important;
    width: 14px !important;
    height: 14px !important;
    opacity: 1 !important;
  }
}
</style>
